import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { getMe } from "@/pages/settings/users";
import type { UserRead, UserRoleRead } from "@/pages/settings/users";

type CurrentUserContextValue = {
  /** The signed-in user's application record from GET /users/me. */
  user: UserRead | null;
  role: UserRoleRead | null;
  loading: boolean;
  error: string | null;
  /** False while role is loading or when the user is a VIEWER. */
  canEdit: boolean;
  isViewer: boolean;
};

const CurrentUserContext = createContext<CurrentUserContextValue | undefined>(
  undefined,
);

export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserRead | null>(null);
  const [role, setRole] = useState<UserRoleRead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadCurrentUser() {
      setLoading(true);
      setError(null);

      try {
        const me = await getMe();
        if (!cancelled) {
          setUser(me);
          setRole(me.role);
        }
      } catch (err) {
        if (!cancelled) {
          setUser(null);
          setRole(null);
          setError(
            err instanceof Error ? err.message : "Failed to load current user",
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadCurrentUser();

    return () => {
      cancelled = true;
    };
  }, []);

  const canEdit = role !== null && role !== "VIEWER";
  const isViewer = role === "VIEWER";

  const value: CurrentUserContextValue = {
    user,
    role,
    loading,
    error,
    canEdit,
    isViewer,
  };

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
}

export function useCurrentUser() {
  const context = useContext(CurrentUserContext);
  if (!context) {
    throw new Error(
      "useCurrentUser must be used inside CurrentUserProvider",
    );
  }
  return context;
}
